import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import { useTheme } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import { forwardRef, memo } from 'react';
import { TableComponents, TableVirtuoso } from 'react-virtuoso';
import { UIVirtualizedTableWrapper } from './style';
import { ColumnProps } from '@/components/ui/WrappedTable/index';
import { getTableRowProps } from '@/components/ui/WrappedTable/helper';

type TableRowData<T extends Record<string | number, any>> = T;

export interface ColumnData<T extends TableRowData<any>> {
  dataKey: keyof T;
  title: React.ReactNode;
  numeric?: boolean;
  width: number | string;
  align?: 'left' | 'right' | 'center';
  render?: (index: number, value: any, row: T) => React.ReactNode;
}

function fixedHeaderContent<T = TableRowData<any>>(
  columns: ColumnProps<TableRowData<any>>[],
) {
  return (
    <TableRow>
      {columns.map((column) => (
        <TableCell
          key={column.dataKey as string | number}
          variant="head"
          align={
            column.align
              ? column.align
              : column.numeric || false
                ? 'right'
                : 'left'
          }
          style={{ width: column.width }}
          sx={{
            borderBottom: 'none',
          }}
        >
          {column.title}
        </TableCell>
      ))}
    </TableRow>
  );
}

const VirtuosoTableComponents: TableComponents<
  TableRowData<any>,
  { fixedHead?: boolean }
> = {
  // eslint-disable-next-line react/display-name
  Scroller: forwardRef<HTMLDivElement>((props, ref) => (
    <TableContainer component={Paper} {...props} ref={ref} />
  )),
  Table: (props) => (
    <Table
      size="small"
      {...props}
      sx={{
        borderCollapse: 'separate',
        tableLayout: 'fixed',
        background: 'transparent',
      }}
    />
  ),
  TableHead: forwardRef(({ style, ...props }, ref) => (
    <thead
      ref={ref}
      {...props}
      style={{
        ...style,
        zIndex: 3,
        position: props.context.fixedHead ? 'sticky' : 'relative',
      }}
    />
  )),
  TableRow: ({ item: _item, ...props }) => {
    return (
      <TableRow
        {...props}
        {...getTableRowProps(_item)}
        // onAnimationEnd={(e) => {
        //   if (e.animationName === sliderIn.name) {
        //     _item.animateItem = false;
        //   }
        // }}
      />
    );
  },
  // eslint-disable-next-line react/display-name
  TableBody: forwardRef<HTMLTableSectionElement>((props, ref) => (
    <TableBody {...props} ref={ref} />
  )),
};

function rowContent<T = TableRowData<any>>(
  _index: number,
  row: TableRowData<any>,
  columns: ColumnProps<T>[],
  loading: boolean,
) {
  return (
    <>
      {columns.map((column, index) => (
        <TableCell
          key={column.dataKey as string | number}
          align={
            column.align
              ? column.align
              : column.numeric || false
                ? 'right'
                : 'left'
          }
          sx={(theme) => ({
            backgroundColor: 'transparent',
            borderBottom: 'none',
            ...theme.typography.subtitle2,
            color: theme.palette.text.primary,
            height: theme.spacing(7),
            width: column.width,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          })}
        >
          {loading ? (
            <Skeleton variant="text" width={column.width} />
          ) : (
            column.render?.(index, row[column.dataKey], row) ||
            row[column.dataKey]
          )}
        </TableCell>
      ))}
    </>
  );
}

export interface VirtualizedTableProps<T = TableRowData<any>> {
  /**
   * height of table
   */
  height: string;
  /**
   * data of table
   */
  data: T[];
  /**
   * columns of table
   */
  columns: ColumnProps<T>[];
  fixedHead?: boolean;
  /**
   * loading state
   */
  loading?: boolean;
  /**
   * show skeleton rows when loading
   */
  skeletonRowCount?: number;
  /**
   * background color
   */
  bgcolor?: string;
}

export function VirtualizedTable<T = TableRowData<any>>(
  props: VirtualizedTableProps<T>,
) {
  const theme = useTheme();
  const {
    data,
    columns,
    height = '400px',
    loading = false,
    skeletonRowCount = 8,
    fixedHead = false,
    bgcolor = theme.palette.grey[800],
  } = props;

  const rowCount = loading ? skeletonRowCount : data.length;

  const skeletonRows = Array.from({ length: rowCount }).map((_, index) => (
    <TableRow key={index}>
      {columns.map((column) => (
        <TableCell
          key={column.dataKey as string | number}
          align={column.align || 'center'}
          sx={{
            backgroundColor: 'transparent',
            borderBottom: 'none',
          }}
        >
          <Skeleton variant="text" width={column.width} />
        </TableCell>
      ))}
    </TableRow>
  ));

  const dataSources = loading ? skeletonRows : data;

  return (
    <UIVirtualizedTableWrapper bgcolor={bgcolor} height={height}>
      <TableVirtuoso<
        any,
        {
          fixedHead?: boolean;
        }
      >
        data={dataSources}
        context={{ fixedHead }}
        components={VirtuosoTableComponents}
        fixedHeaderContent={() => fixedHeaderContent(columns as any)}
        itemContent={(index, tableData) =>
          rowContent(index, tableData, columns, loading)
        }
      />
    </UIVirtualizedTableWrapper>
  );
}

VirtualizedTable.displayName = 'VirtualizedTable';

export default memo(VirtualizedTable) as typeof VirtualizedTable;
